import { BACKEND } from '../config';
import { reportSessionExpired } from '../utils/sessionExpiry';
import { composeTimeoutSignal, makeFetchTimeoutError } from '../utils/fetchTimeout';

let csrfMemory = '';
let csrfInitPromise: Promise<void> | null = null;

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

// FE-10 (docs/quality/FRONTEND-AUDIT-2026-07-31.md): utils/csrfFetch.ts несёт таймаут-
// детектор зависшего запроса (DEFAULT_TIMEOUT_MS=120000 — «без него зависший запрос
// держит спиннер страницы бесконечно»), а этот второй, независимый стек fetch (apiJson/
// apiForm, потребители — pages/kanban.tsx→KanbanAPI/TasksPage, App.tsx поллинг, lib/
// pushChannel.ts, lib/softSyncTelemetry.ts, TimesheetPage.tsx) его не имел вовсе.
// Свести транспорт на csrfFetch (utils/csrfFetch.ts импортирует getCsrfToken ОТСЮДА —
// это сделало бы модули циклическими) — корневой фикс, но выходит за пределы зоны этой
// правки; здесь патч — тот же бюджет и то же имя ошибки ('TimeoutError'), без изменения
// остального поведения apiClient (retry на CSRF-403, parseApiError, X-Requested-With).
//
// Раунд правок №2: makeTimeoutError и AbortController/таймер/cleanup раньше были
// продублированы дословно из csrfFetch.ts — вынесены в нейтральный utils/fetchTimeout.ts
// (не импортирует ни apiClient, ни csrfFetch — цикла нет), апи-клиент теперь источник
// один. csrfFetch.ts вне зоны этой правки — миграция его на тот же модуль остаётся
// отдельной задачей (см. комментарий в utils/fetchTimeout.ts).
const DEFAULT_TIMEOUT_MS = 120_000;

// Заливка файлов (apiForm/FormData) — длительность канала, а не отзывчивости сервера;
// фиксированный бюджет там неприменим (тот же принцип, что isStreamedBody в csrfFetch.ts).
const fetchWithTimeout = (url: string, init: RequestInit, budgetMs: number): Promise<Response> => {
  if (budgetMs <= 0) return fetch(url, init);

  const { signal, cleanup, isTimedOut } = composeTimeoutSignal(init.signal ?? undefined, budgetMs);

  return fetch(url, { ...init, signal })
    .then((res) => {
      cleanup();
      return res;
    })
    .catch((err) => {
      cleanup();
      if (isTimedOut()) throw makeFetchTimeoutError(url, budgetMs);
      throw err;
    });
};

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const part = document.cookie
    .split(';')
    .map((chunk) => chunk.trim())
    .find((chunk) => chunk.startsWith(`${name}=`));
  if (!part) return null;
  const value = part.slice(name.length + 1);
  return value ? decodeURIComponent(value) : null;
};

const needsCsrf = (method = 'GET') => !SAFE_METHODS.has(method.toUpperCase());

const ensureCsrf = async (force = false): Promise<void> => {
  if (csrfMemory && !force) return;
  if (!csrfInitPromise) {
    csrfInitPromise = fetch(`${BACKEND}/api/csrf/`, { credentials: 'include' })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        csrfMemory = payload?.csrftoken || payload?.csrfToken || getCookie('csrftoken') || '';
      })
      .catch(() => {})
      .finally(() => {
        csrfInitPromise = null;
      });
  }
  await csrfInitPromise;
};

export const primeCsrf = async (): Promise<void> => {
  await ensureCsrf();
};

// Токен из памяти — для csrfFetch/safeFetch, когда cookie недоступна (desktop-EXE на file://).
// Заполняется через primeCsrf()/ensureCsrf() из тела /api/csrf/.
export const getCsrfToken = (): string => csrfMemory;

const parseApiError = async (res: Response): Promise<string> => {
  try {
    const j = await res.clone().json();
    return (j?.detail || j?.error || j?.reason) ?? JSON.stringify(j);
  } catch {
    return (await res.text().catch(() => `${res.status}`)) || `${res.status}`;
  }
};

const request = async <T>(url: string, opts?: RequestInit, isForm = false): Promise<T> => {
  const method = (opts?.method || 'GET').toUpperCase();

  if (needsCsrf(method) && !csrfMemory) {
    await ensureCsrf();
  }

  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
    ...(isForm ? {} : { 'Content-Type': 'application/json' }),
    ...(opts?.headers as Record<string, string> | undefined),
  };

  if (needsCsrf(method) && csrfMemory) headers['X-CSRFToken'] = csrfMemory;

  // Формы (загрузка файлов) — без ограничения по времени, как isStreamedBody в csrfFetch.ts.
  const budgetMs = isForm ? 0 : DEFAULT_TIMEOUT_MS;
  const doFetch = () =>
    fetchWithTimeout(
      url,
      {
        credentials: 'include',
        ...opts,
        headers,
      },
      budgetMs
    );

  let res = await doFetch();

  if (res.status === 403) {
    const text = await res
      .clone()
      .text()
      .catch(() => '');
    if (text.toLowerCase().includes('csrf')) {
      await ensureCsrf(true);
      if (csrfMemory) {
        headers['X-CSRFToken'] = csrfMemory;
        res = await doFetch();
      }
    }
  }

  if (res.status === 401 && !url.includes('/api/login')) {
    // Истёкшая сессия — глобальный репорт (см. utils/sessionExpiry.ts)
    reportSessionExpired('apiClient:' + url.slice(0, 80));
  }

  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
};

export const apiJson = <T = unknown>(url: string, opts?: RequestInit) =>
  request<T>(url, opts, false);
export const apiForm = <T = unknown>(url: string, opts?: RequestInit) =>
  request<T>(url, opts, true);
